import React from "react"
import { Form, FastField, withFormik } from "formik"
import { parsePhoneNumber } from "libphonenumber-js"
import * as Yup from "yup"
import {
  PasswordField,
  CheckBox,
  MobileNumberField
} from "../../../common/Fields"
import "./EmailLoginForm.css"
import { API, API_PUBLIC } from "../../../api"

function BaseEmailLoginForm({
  isSubmitting,
  handleForgotPassword,
  backHandler,
  errors,
  touched,
  values,
  isNewOrder
}) {
  return (
    <Form className="EmailLoginForm">
      <h1 className="header">Customer Information </h1>

      <span>
        <label>Mobile</label>
        <MobileNumberField
          groupName="mobile"
          mobileNumberName="mobileNumber"
          countryCodeName="countryCode"
          errors={errors}
          touched={touched}
          values={values}
          disabled={!isNewOrder}
        />
      </span>
      <span>
        <label>Password</label>
        <FastField component={PasswordField} name="password" type="password" />
      </span>
      <div className="remember">
        <span>
          <FastField
            component={CheckBox}
            name="remember"
            placeholder="Remember Me"
          />
        </span>
        <button
          className="button-link"
          type="button"
          onClick={handleForgotPassword}
        >
          Forgot Password
        </button>
      </div>
      <button type="submit" className="black" disabled={isSubmitting}>
        {isSubmitting ? "Logging in..." : "Login"}
      </button>
      <button
        type="button"
        className="gray"
        onClick={backHandler}
        disabled={isSubmitting}
      >
        Back
      </button>
    </Form>
  )
}

const FormikEmailLoginForm = withFormik({
  mapPropsToValues: ({ customerData }) => {
    return {
      mobile: (customerData &&
        customerData.mobile &&
        customerData.mobile.mobileNumber &&
        customerData.mobile.countryCode && {
          mobileNumber: customerData.mobile.mobileNumber,
          countryCode: customerData.mobile.countryCode
        }) || { mobileNumber: "", countryCode: "" },
      password: "",
      remember: false
    }
  },
  validationSchema: Yup.object().shape({
    mobile: Yup.object()
      .shape({
        countryCode: Yup.string()
          .matches(
            /^($)|(\+?\d{1,3}$|\d{1,4}$)/,
            "Please enter a valid country code."
          )
          .max(4, "Please enter a valid country code.")
          .typeError("Country code and mobile number should be filled.")
          .required("Please enter a country code"),
        mobileNumber: Yup.string()
          .matches(/^($)|([0-9]{4,13}$)/, "Please enter a valid mobile number.")
          .max(13, "Please enter a valid mobile number.")
          .required("Please enter a mobile number")
          .typeError("Country code and mobile number should be filled.")
      })
      .test("test-phone", "Mobile number is invalid.", function(mobile) {
        if (`${mobile.countryCode}${mobile.mobileNumber}` !== "") {
          try {
            parsePhoneNumber(`${mobile.countryCode}${mobile.mobileNumber}`)
            return true
          } catch (e) {
            return false
          }
        }
        return true
      }),
    password: Yup.string()
      .required("Please enter a password.")
      .min(8, "Password be at least 8 characters.")
  }),
  handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
    try {
      const checkMobile = await API_PUBLIC.get(
        `/customers/${
          props.isNewOrder ? props.newOrderAccountUuid : props.account_uuid
        }/${values.mobile.countryCode}${values.mobile.mobileNumber}`
      )
      const {
        data: { has_account }
      } = checkMobile
      if (!has_account) {
        throw new Error("customers/mobile-does-not-exist")
      }
      const loginUrl = `/customers/${
        props.isNewOrder
          ? props.newOrderAccountUuid
          : props.account_uuid && props.account_uuid
      }/login`
      const response = await API.post(
        loginUrl,
        JSON.stringify({
          mobile: `${values.mobile.countryCode}${values.mobile.mobileNumber}`,
          pass: values.password
        })
      )
      const customer = response.data.customer
      const adr = customer.address
      props.setCustomerAddress({
        addressOne: adr.address_line_1 || "",
        addressTwo: adr.address_line_2 || "",
        barangay:
          (adr.barangay && { value: adr.barangay, label: adr.barangay }) || "",
        city:
          (adr.country.toLowerCase() === "philippines" && {
            value: adr.city,
            label: adr.city
          }) ||
          adr.city ||
          "",
        country:
          (adr.country && { value: adr.country, label: adr.country }) || "",
        state:
          (adr.province && { value: adr.province, label: adr.province }) || "",
        zipcode: adr.zipcode || ""
      })
      // props.setCustomerStatus(
      //   !response.data.has_account,
      //   response.data.customer.id
      // )
      props.setCustomerData({
        firstName: customer.firstname,
        lastName: customer.lastname,
        mobile: {
          countryCode: customer.mobile_country_code,
          mobileNumber: customer.mobile
        },
        email: customer.email,
        method: "email",
        id: customer.id
      })
      props.setActiveStep("shipping")
      props.setCheckoutMethod("email")
    } catch (error) {
      console.log(error.response || error)
      if (error && error.message === "customers/mobile-does-not-exist") {
        setErrors({
          mobile: "The mobile number you entered is currently not in use."
        })
      }
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message === "UNVERIFIED_EMAIL!"
      ) {
        const emailAdd =
          (error &&
            error.response &&
            error.response.data &&
            error.response.data.email) ||
          ""
        const mobile =
          (error &&
            error.response &&
            error.response.data &&
            `${error.response.data.country_code}${
              error.response.data.mobile
            }`) ||
          ""
        props.handleShowVerifyEmail(emailAdd, mobile)
      } else if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message === "Account not found!"
      ) {
        setErrors({
          mobile: "The store does not exist."
        })
      } else if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message === "INVALID_CREDENTIALS!"
      ) {
        setErrors({
          mobile: "",
          password: "The mobile or password you used is invalid"
        })
      }
    } finally {
      setSubmitting(false)
    }
  }
})(BaseEmailLoginForm)

function LoggedIn({ customerData, setActiveStep, backHandler }) {
  function proceed() {
    setActiveStep("shipping")
  }
  return (
    <div className="LoggedIn">
      <p>Logged in as {customerData.email}.</p>
      <button type="button" className="black" onClick={proceed}>
        Proceed to shipping method
      </button>
      <button type="button" className="gray" onClick={backHandler}>
        Back
      </button>
    </div>
  )
}

function EmailLoginForm(props) {
  return props.checkoutMethod === "email" ? (
    <LoggedIn {...props} />
  ) : (
    <FormikEmailLoginForm {...props} />
  )
}

export default EmailLoginForm
